import BoardEmptyState from '@/components/BoardEmptyState';
import ConfirmDialog from '@/components/ConfirmDialog';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { EvaluationLesson, Lesson } from '@/models/Lesson';
import { LessonData } from '@/models/LessonData';
import { ApplicationState } from '@/store';
import { setFloatingCourseActionsConfiguration } from '@/store/common/actions';
import { FloatingCourseActionsBarConfiguration } from '@/store/common/types';
import {
    getLessonClear,
    getLMSLessonsRequest,
    postEvaluationRequest,
    putLessonReorderRequest,
    setCurrentLessonForEdition,
} from '@/store/lesson/action';
import { getScormPackageStatusClear, getScormPackageStatusRequest, publishListClear, publishListRequest } from '@/store/list/action';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, DraggableProvided, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import EvaluationCard from './EvaluationCard';
import LessonCard from './LessonCard';
import './style.scss';

interface MediaTypesConfiguration {
    id: number;
    icon: string;
    name: string;
    type: string;
    mediaType: string;
}

const CourseLessons: React.FC = () => {
    const { loading, lessons, strings, currentLessonInEdition, success, currentCourseInEdition, course, scormPackageStatus, adminUser } = useSelector(
        (state: ApplicationState) => ({
            loading: state.lessons.loading,
            lessons: state.lessons.data.lessons || [],
            strings: state.locale.data,
            currentLessonInEdition: state.lessons.currentData,
            success: state.lessons.success,
            currentCourseInEdition: state.lists.courseData,
            course: state.lists.courseData,
            scormPackageStatus: state.lists.scormPackageStatus,
            adminUser: state.identity.data.user.admin,
        }),
    );

    const [initialLoading, setInitialLoading] = useState(true);
    const [mediaTypes, setMediaTypes] = useState<MediaTypesConfiguration[]>([]);
    const [lessonList, setLessonList] = useState<Lesson[]>([]);
    const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
    const [onPrevPressed, setOnPrevPressed] = useState<boolean>(false);
    const [onNextPressed, setOnNextPressed] = useState<boolean>(false);
    const [onSavePressed, setOnSavePressed] = useState<boolean>(false);
    const [publishAllowed, setPublishAllowed] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [onShowModal, setOnShowModal] = useState<boolean>(false);

    const history = useHistory();
    const dispatch = useDispatch();
    const courseId: number = parseInt(useParams<{ id: string }>().id);

    useEffect(() => {
        if (courseId && course?.organizationId === null && !adminUser) {
            history.push('/contents/lms/courses');
        }
    }, [courseId, course?.organizationId, adminUser]);

    useEffect(() => {
        dispatch(publishListClear());
        return () => {
            dispatch(getLessonClear());
        };
    }, []);

    useEffect(() => {
        dispatch(getLMSLessonsRequest(courseId));

        setMediaTypes([
            {
                id: 1,
                icon: Constants.icons.VIDEOCAM,
                name: strings.VIDEO,
                type: 'MediaLesson',
                mediaType: 'VIDEO',
            },
            {
                id: 2,
                icon: Constants.icons.FILE_DOCUMENT_OUTLINE,
                name: strings.ARTICLE,
                type: 'MediaLesson',
                mediaType: 'HTML',
            },
            {
                id: 3,
                icon: Constants.icons.MICROPHONE,
                name: strings.PODCAST,
                type: 'MediaLesson',
                mediaType: 'AUDIO',
            },
            {
                id: 4,
                icon: Constants.icons.LINK,
                name: strings.LINK,
                type: 'MediaLesson',
                mediaType: 'EXTERNAL',
            },
            {
                id: 5,
                icon: Constants.icons.LIST_ALT,
                name: strings.EVALUATION,
                type: 'EvaluationLesson',
                mediaType: '',
            },
            {
                id: 6,
                icon: Constants.icons.FILE_COPY,
                name: strings.DOCUMENT,
                type: 'MediaLesson',
                mediaType: 'DOCUMENT',
            },
            {
                id: 7,
                icon: Constants.icons.IMAGE_OUTLINE,
                name: strings.INFOGRAPHIC,
                type: 'MediaLesson',
                mediaType: 'CHART',
            },
        ]);
    }, [dispatch, strings._LANGUAGE, courseId, success]);

    useEffect(() => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                currentStep: 2,
                nextButtonTitle: strings.PUBLISH,
                onNext: () => setOnNextPressed(true),
                onPrevious: () => setOnPrevPressed(true),
                onSave: () => setOnSavePressed(true),
                saveDisabled: false,
                previousButtonTitle: strings.PREVIOUS,
                saveButtonTitle: strings.SAVE_AND_CLOSE,
                totalSteps: 2,
                previewDisabled: false,
                nextDisabled: !publishAllowed,
                blockUi: false,
            }),
        );
    }, [publishAllowed, strings._LANGUAGE]);

    useEffect(() => {
        if (lessons.length > 0) {
            setLessonList(lessons);
            setHasPendingChanges(false);
        }
    }, [lessons]);

    useEffect(() => {
        if (!loading) setInitialLoading(false);
    }, [loading]);

    useEffect(() => {
        if (!currentLessonInEdition) {
            if (onPrevPressed) {
                dispatch(
                    setFloatingCourseActionsConfiguration({
                        previousButtonTitle: strings.LOADING,
                        blockUi: true,
                    } as FloatingCourseActionsBarConfiguration),
                );
                history.push(`/contents/lms/courses/${courseId}`);
            }

            if (onSavePressed) {
                history.push(`/contents/lms/courses`);
            }

            if (onNextPressed) {
                if (course && course.scormPackageName) {
                    dispatch(getScormPackageStatusRequest({ courseId: courseId, showToast: false }));
                } else {
                    actionModalPrepareToShow();
                }
            }
        }
    }, [onSavePressed, onNextPressed, onPrevPressed, currentLessonInEdition]);

    const actionModalPrepareToShow = () => {
        setOnShowModal(true);
        dispatch(publishListRequest(courseId));
    };

    useEffect(() => {
        if (scormPackageStatus && onNextPressed) {
            if (scormPackageStatus.status === Constants.scormStatus.SUCCESS) {
                actionModalPrepareToShow();
            } else {
                setShowConfirmDialog(true);
                setOnNextPressed(false);
            }
        }
    }, [scormPackageStatus]);

    useEffect(() => {
        setPublishAllowed(
            (course.type === 'REGULAR' &&
                course.status === Constants.courseStatus.DRAFT &&
                !currentLessonInEdition &&
                lessons.length > 0 &&
                lessons.filter(
                    (lesson) =>
                        lesson.type === Constants.lesson.type.EvaluationLesson &&
                        (lesson.content.name === '' || (lesson as EvaluationLesson).questionsAmount === 0),
                ).length === 0) ||
                (course.type === 'WORKSHOP' && !course.published && !currentLessonInEdition),
        );
    }, [course, lessons, currentLessonInEdition]);

    const handleMediaTypeClick = (mediaType: MediaTypesConfiguration) => {
        if (!currentLessonInEdition) {
            const newLesson = {
                type: mediaType.type,
                mediaType: mediaType.mediaType,
                lessonOrder: lessonList && lessonList.length > 0 ? lessonList.length : 0,
            } as Lesson;
            setHasPendingChanges(true);
            setLessonList([...lessonList, newLesson]);
            if (mediaType.type === 'EvaluationLesson') {
                saveEvaluation();
            } else {
                dispatch(setCurrentLessonForEdition(newLesson));
            }
        }
    };

    const saveEvaluation = () => {
        const data = {
            evaluationLesson: {
                '@class': '.LessonContentTO',
                applicationId: course.applicationId,
                author: '',
                course: { id: course.id },
                description: '',
                gradeWeight: 1,
                incGradeWeight: true,
                incTimeLimit: false,
                lessonOrder: null,
                locale: 'pt',
                lockAfterApproval: false,
                mainImage: undefined,
                mandatory: true,
                masteryScore: 70,
                maxTries: 1,
                mediaType: null,
                name: '',
                organizationId: course.organizationId,
                prerequisites: false,
                saveHighestScore: false,
                showCoverImage: false,
                showResponseSummary: false,
                shuffleAlternatives: false,
                timeLimit: 0,
                type: 'EvaluationLesson',
                workload: 0,
            },
        } as unknown;

        dispatch(postEvaluationRequest(data as LessonData));
    };

    const onDeleteHandler = (lessonId: number) => {
        lessonList.splice(
            lessonList.findIndex((l) => !l.id || l.id === lessonId),
            1,
        );
        dispatch(setCurrentLessonForEdition(undefined));
        setHasPendingChanges(false);
    };

    const onDragEnd = (result: DropResult): void => {
        const { source, destination } = result;
        if (destination && source.droppableId === destination.droppableId) {
            const result = [...lessonList];
            const [movedLesson]: Lesson[] = result.splice(source.index, 1);
            const lessonOrder = result[destination.index] ? result[destination.index].lessonOrder : result.length;
            dispatch(putLessonReorderRequest(movedLesson.id, lessonOrder));
            result.splice(destination.index, 0, movedLesson);
            setLessonList(
                result.map((e, index) => {
                    e.lessonOrder = index;
                    return e;
                }),
            );
        }
    };

    return (
        <>
            {initialLoading ? (
                <Loader />
            ) : (
                <>
                    <ConfirmDialog
                        content={
                            scormPackageStatus && scormPackageStatus.status === Constants.scormStatus.ERROR
                                ? strings.SCORM_PACKAGE_PROCESSING_ERROR_ALERT
                                : strings.SCORM_PACKAGE_PROCESSING_ALERT
                        }
                        onExited={() => dispatch(getScormPackageStatusClear())}
                        rightButtonTitle={strings.UNDERSTAND}
                        rightClick={() => setShowConfirmDialog(false)}
                        show={showConfirmDialog}
                    />

                    <ConfirmDialog
                        color={'#3f9b35'}
                        content={course.type === 'WORKSHOP' ? strings.ATTENTION_WORKSHOP_PUBLISHED : strings.ATTENTION_COURSE_PUBLISHED}
                        icon={Constants.icons.CHECK_CIRCLE}
                        leftButtonTitle={strings.NO}
                        leftClick={() => history.push(`/contents/lms/courses`)}
                        rightButtonTitle={strings.YES}
                        rightClick={() => history.push(`/contents/lms/courses/${courseId}/classes`)}
                        show={onShowModal}
                        title={course.type === 'WORKSHOP' ? strings.WORKSHOP_PUBLISHED : strings.COURSE_PUBLISH}
                    />

                    <div className="course-lessons-main-container">
                        <div className="title-container">
                            <span>{strings.LESSON_BOARD}</span>
                        </div>
                        <div className="lesson-container">
                            <div className="lesson-board">
                                {lessonList.length === 0 ? (
                                    <BoardEmptyState message={strings.EMPTY_LESSON_BOARD_MESSAGE} title={strings.EMPTY_LESSON_BOARD_TITLE} />
                                ) : currentLessonInEdition !== undefined ? (
                                    lessonList.map((item, index) => (
                                        <div className="draggable-list-item non-draggable" key={item.id || index}>
                                            {[Constants.lesson.type.MediaLesson, Constants.lesson.type.ScormLesson].includes(item.type) && (
                                                <LessonCard
                                                    key={item.id || index}
                                                    lesson={item}
                                                    lessonLength={lessonList.length}
                                                    onDelete={onDeleteHandler}
                                                    onNextPressed={onNextPressed}
                                                    onPrevPressed={onPrevPressed}
                                                    onSavePressed={onSavePressed}
                                                    preRequisitesEnabled={index > 0}
                                                />
                                            )}
                                            {item.type === Constants.lesson.type.EvaluationLesson && (
                                                <EvaluationCard
                                                    key={item.id || index}
                                                    lesson={item as EvaluationLesson}
                                                    lessonLength={lessonList.length}
                                                    onDelete={onDeleteHandler}
                                                    onNextPressed={onNextPressed}
                                                    onPrevPressed={onPrevPressed}
                                                    onSavePressed={onSavePressed}
                                                    preRequisitesEnabled={index > 0}
                                                />
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <DragDropContext onDragEnd={onDragEnd}>
                                        <Droppable droppableId="droppable">
                                            {(provided: DroppableProvided) => (
                                                <div ref={provided.innerRef} {...provided.droppableProps} className="draggable-list">
                                                    {lessonList && lessonList.length > 0 && (
                                                        <div className="list-lists">
                                                            {lessonList &&
                                                                lessonList.map(
                                                                    (item, index) =>
                                                                        item.id && (
                                                                            <Draggable
                                                                                draggableId={
                                                                                    (item.id && item.id.toString()) || item.type + item.mediaType
                                                                                }
                                                                                index={index}
                                                                                key={item.id}
                                                                            >
                                                                                {(providedDraggable: DraggableProvided) => (
                                                                                    <div
                                                                                        ref={providedDraggable.innerRef}
                                                                                        {...providedDraggable.draggableProps}
                                                                                        {...providedDraggable.dragHandleProps}
                                                                                        className="draggable-list-item"
                                                                                    >
                                                                                        <i className="material-icons-outlined align-middle drag-indicator-icon">
                                                                                            drag_indicator
                                                                                        </i>

                                                                                        <span className="draggable-list-item-card">
                                                                                            {[
                                                                                                Constants.lesson.type.MediaLesson,
                                                                                                Constants.lesson.type.ScormLesson,
                                                                                            ].includes(item.type) && (
                                                                                                <LessonCard
                                                                                                    key={item.id || index}
                                                                                                    lesson={item}
                                                                                                    lessonLength={lessonList.length}
                                                                                                    onDelete={onDeleteHandler}
                                                                                                    onNextPressed={onNextPressed}
                                                                                                    onPrevPressed={onPrevPressed}
                                                                                                    onSavePressed={onSavePressed}
                                                                                                    preRequisitesEnabled={index > 0}
                                                                                                />
                                                                                            )}
                                                                                            {item.type === Constants.lesson.type.EvaluationLesson && (
                                                                                                <EvaluationCard
                                                                                                    key={item.id || index}
                                                                                                    lesson={item as EvaluationLesson}
                                                                                                    lessonLength={lessonList.length}
                                                                                                    onDelete={onDeleteHandler}
                                                                                                    onNextPressed={onNextPressed}
                                                                                                    onPrevPressed={onPrevPressed}
                                                                                                    onSavePressed={onSavePressed}
                                                                                                    preRequisitesEnabled={index > 0}
                                                                                                />
                                                                                            )}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </Draggable>
                                                                        ),
                                                                )}
                                                        </div>
                                                    )}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </div>
                            <div className="media-types-menu">
                                {mediaTypes.map((mediaType: MediaTypesConfiguration) => (
                                    <button
                                        className={`btn btn-link media-types ${
                                            !currentLessonInEdition && currentCourseInEdition.status === Constants.courseStatus.DRAFT ? 'active' : ''
                                        }`}
                                        disabled={
                                            hasPendingChanges ||
                                            !!currentLessonInEdition ||
                                            currentCourseInEdition.status !== Constants.courseStatus.DRAFT
                                        }
                                        key={mediaType.id}
                                        onClick={() => handleMediaTypeClick(mediaType)}
                                    >
                                        <i className="material-icons-outlined align-middle icon-plus">{Constants.icons.ADD}</i>
                                        <i className="material-icons-outlined align-middle media-type-icon">{mediaType.icon}</i>
                                        <span className="media-type-text">{mediaType.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default CourseLessons;
